import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload, Link2 } from "lucide-react";
import { toast } from "sonner";
import { adminFetch } from "@/utils/adminFetch";

export default function AdminVideosManager() {
  const [videos, setVideos] = useState([]);
  const [newTitleIt, setNewTitleIt] = useState("");
  const [newTitleEn, setNewTitleEn] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setVideos)
      .catch(() => {});
  };
  useEffect(load, []);

  const saveField = async (v, payload) => {
    try {
      await adminFetch(`/admin/videos/${v.video_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      toast.success("Video aggiornato");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Eliminare "${v.title.it}"?`)) return;
    try {
      await adminFetch(`/admin/videos/${v.video_id}`, { method: "DELETE" });
      setVideos((vs) => vs.filter((x) => x.video_id !== v.video_id));
      toast.success("Video eliminato");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const uploadToPlaceholder = async (v, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await adminFetch("/admin/upload", { method: "POST", body: fd });
      await adminFetch(`/admin/videos/${v.video_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ src: up.url }),
      });
      toast.success("Video caricato");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const add = async () => {
    if (!newTitleIt.trim() || (!newFile && !newUrl.trim())) {
      toast.error("Servono un titolo (IT) e un file video o un URL");
      return;
    }
    setAdding(true);
    try {
      let src = newUrl.trim();
      if (newFile) {
        const fd = new FormData();
        fd.append("file", newFile);
        const up = await adminFetch("/admin/upload", { method: "POST", body: fd });
        src = up.url;
      }
      const doc = await adminFetch("/admin/videos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ src, title_it: newTitleIt.trim(), title_en: newTitleEn.trim() }),
      });
      setVideos((vs) => [...vs, doc]);
      setNewFile(null);
      setNewUrl("");
      setNewTitleIt("");
      setNewTitleEn("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Video aggiunto alla Gallery");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="mt-14" data-testid="admin-videos">
      <h3 className="font-serif text-2xl text-ink mb-6">Video della Gallery</h3>

      <div className="border border-stone2/60 p-5 mb-8" data-testid="admin-video-add-form">
        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink/50 mb-4">Aggiungi un video</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">File video (max 200MB)</label>
            <input
              ref={fileRef}
              type="file"
              accept="video/*"
              onChange={(e) => { setNewFile(e.target.files?.[0] || null); if (e.target.files?.[0]) setNewUrl(""); }}
              data-testid="admin-video-file"
              className="mt-1.5 block text-sm text-ink/60 file:mr-3 file:border file:border-stone2 file:bg-ivory file:px-3 file:py-2 file:text-xs file:uppercase file:tracking-[0.1em] hover:file:border-medblue"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">oppure URL video</label>
            <input
              placeholder="https://..."
              value={newUrl}
              onChange={(e) => { setNewUrl(e.target.value); if (e.target.value) setNewFile(null); if (fileRef.current) fileRef.current.value = ""; }}
              data-testid="admin-video-url"
              className="mt-1.5 w-64 border border-stone2 bg-ivory px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-medblue"
            />
          </div>
          <input
            placeholder="Titolo (IT) *"
            value={newTitleIt}
            onChange={(e) => setNewTitleIt(e.target.value)}
            data-testid="admin-video-title-it"
            className="border border-stone2 bg-ivory px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-medblue"
          />
          <input
            placeholder="Titolo (EN)"
            value={newTitleEn}
            onChange={(e) => setNewTitleEn(e.target.value)}
            data-testid="admin-video-title-en"
            className="border border-stone2 bg-ivory px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-medblue"
          />
          <button
            type="button"
            onClick={add}
            disabled={adding}
            data-testid="admin-video-add-button"
            className="inline-flex items-center gap-2 bg-medblue text-ivory text-[10px] font-medium uppercase tracking-[0.15em] px-5 py-3 hover:bg-ink disabled:opacity-40 transition-colors duration-300"
          >
            {adding ? <Upload size={13} className="animate-pulse" /> : <Plus size={13} />} Aggiungi
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.map((v) => (
          <div key={v.video_id} className={`border ${v.kind === "placeholder" ? "border-dashed" : ""} border-stone2/60 bg-ivory`} data-testid={`admin-video-${v.video_id}`}>
            <div className="aspect-video bg-ink/5 flex items-center justify-center overflow-hidden">
              {v.kind === "video" && v.src ? (
                <video preload="metadata" poster={v.poster || undefined} className="w-full h-full object-cover">
                  <source src={v.src} />
                </video>
              ) : (
                <label className="cursor-pointer text-center px-4">
                  <Upload size={20} strokeWidth={1.5} className="text-medblue mx-auto mb-2" />
                  <span className="text-[10px] uppercase tracking-[0.15em] text-medblue">Carica il video</span>
                  <input type="file" accept="video/*" className="hidden" data-testid={`admin-video-upload-${v.video_id}`} onChange={(e) => uploadToPlaceholder(v, e.target.files?.[0])} />
                </label>
              )}
            </div>
            <div className="p-3 space-y-2">
              <input
                defaultValue={v.title.it}
                onBlur={(e) => e.target.value !== v.title.it && saveField(v, { title_it: e.target.value })}
                data-testid={`admin-video-title-it-${v.video_id}`}
                className="w-full border border-stone2/60 bg-ivory px-2.5 py-1.5 text-xs text-ink focus:outline-none focus:border-medblue"
              />
              <input
                defaultValue={v.title.en}
                onBlur={(e) => e.target.value !== v.title.en && saveField(v, { title_en: e.target.value })}
                data-testid={`admin-video-title-en-${v.video_id}`}
                className="w-full border border-stone2/60 bg-ivory px-2.5 py-1.5 text-xs text-ink/60 focus:outline-none focus:border-medblue"
              />
              <button
                type="button"
                onClick={() => remove(v)}
                data-testid={`admin-video-delete-${v.video_id}`}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-red-600/70 hover:text-red-600 transition-colors"
              >
                <Trash2 size={11} /> Elimina
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
